import type { ClientProject, ClientProjectSummary } from './api';
import {
  compareProjectUpdatedAt,
  mergeProjectSummaries,
  projectSummaryFromDetail,
} from './project-list';
import {
  ProjectRevisionLedger,
  createChildProjectRequestScope,
  isChildProjectRequestScopeCurrent,
  type ChildProjectRequestScope,
} from './project-revisions';

export type ChildProjectScope = ChildProjectRequestScope;

export type CurrentChildProject = Readonly<{
  identityEpoch: number;
  routeProjectId?: string;
  project?: Readonly<{ id: string; updatedAt: string }>;
}>;

type AcceptedProjectSummary = Readonly<{
  summary: Readonly<ClientProjectSummary>;
  canInsert: boolean;
}>;

export type AcceptedProjectSummaryBatch = Readonly<{
  entries: readonly AcceptedProjectSummary[];
}>;

function immutableSummary(project: ClientProjectSummary) {
  return Object.freeze({ ...project });
}

export function canApplyChildProjectResult(
  scope: ChildProjectScope,
  current: CurrentChildProject,
) {
  return isChildProjectRequestScopeCurrent(scope, current);
}

export function applyScopedChildProjectResult<T>(
  scope: ChildProjectScope,
  current: CurrentChildProject,
  apply: () => T,
): T | undefined {
  if (!canApplyChildProjectResult(scope, current)) return undefined;
  return apply();
}

export class ProjectResponseCoordinator {
  private readonly latestAcceptedSummaries = new Map<string, Readonly<ClientProjectSummary>>();

  constructor(private readonly ledger = new ProjectRevisionLedger()) {}

  acceptIncomingSummaries(
    incoming: readonly ClientProjectSummary[],
  ): AcceptedProjectSummaryBatch {
    const acceptedById = new Map<string, AcceptedProjectSummary>();

    for (const project of incoming) {
      const floor = this.ledger.floor(project.id);
      if (floor === undefined || compareProjectUpdatedAt(project.updatedAt, floor) >= 0) {
        this.ledger.advance(project.id, project.updatedAt);
        const accepted = immutableSummary(project);
        this.latestAcceptedSummaries.set(project.id, accepted);
        acceptedById.set(project.id, Object.freeze({ summary: accepted, canInsert: true }));
        continue;
      }

      const retained = this.latestAcceptedSummaries.get(project.id);
      if (retained && compareProjectUpdatedAt(retained.updatedAt, floor) >= 0) {
        acceptedById.set(project.id, Object.freeze({ summary: retained, canInsert: false }));
      }
    }

    return Object.freeze({ entries: Object.freeze([...acceptedById.values()]) });
  }

  reconcileFirstPage(
    accepted: AcceptedProjectSummaryBatch,
    known: readonly ClientProjectSummary[],
  ) {
    const sanitized = accepted.entries.map(({ summary }) => summary);
    const representedIds = new Set(sanitized.map((project) => project.id));
    const representedKnown = known.filter((project) => representedIds.has(project.id));
    return mergeProjectSummaries(representedKnown, sanitized);
  }

  mergeAppend(
    accepted: AcceptedProjectSummaryBatch,
    known: readonly ClientProjectSummary[],
  ) {
    const knownIds = new Set(known.map((project) => project.id));
    const sanitized = accepted.entries
      .filter(({ summary, canInsert }) => canInsert || knownIds.has(summary.id))
      .map(({ summary }) => summary);
    return mergeProjectSummaries(known, sanitized);
  }

  mergeDetail(
    summary: ClientProjectSummary,
    known: readonly ClientProjectSummary[],
    allowInsert = false,
  ) {
    if (!allowInsert && !known.some((project) => project.id === summary.id)) return [...known];
    return mergeProjectSummaries(known, [summary]);
  }

  canAcceptDetail(project: Pick<ClientProject, 'id' | 'updatedAt'>) {
    return this.ledger.accepts(project.id, project.updatedAt);
  }

  acceptDetail(project: ClientProject) {
    if (!this.canAcceptDetail(project)) return undefined;
    this.ledger.advance(project.id, project.updatedAt);
    const accepted = immutableSummary(projectSummaryFromDetail(project));
    this.latestAcceptedSummaries.set(project.id, accepted);
    return accepted;
  }

  createChildScope(identityEpoch: number, project: ClientProject): ChildProjectScope {
    return createChildProjectRequestScope(
      identityEpoch,
      project.id,
      project.updatedAt,
      project,
    );
  }

  isChildScopeCurrent(scope: ChildProjectScope, current: CurrentChildProject) {
    return canApplyChildProjectResult(scope, current);
  }

  clearProject(projectId: string) {
    this.ledger.remove(projectId);
    this.latestAcceptedSummaries.delete(projectId);
  }

  clearAccount() {
    this.ledger.clear();
    this.latestAcceptedSummaries.clear();
  }
}
